import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { RentalObjectBundle, RentalObject } from "../../types/api-types";

interface BundleListCardProps {
  rentalObject: RentalObject;
  bundles: RentalObjectBundle[];
  onRemoveBundle: (bundleId: string, objectId: string) => void;
  onEditBundle: (bundle: RentalObjectBundle) => void;
}

const BundleListCard: React.FC<BundleListCardProps> = ({
  rentalObject,
  bundles,
  onRemoveBundle,
  onEditBundle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const getTypeDisplayName = (type: string) => {
    const names: Record<string, string> = {
      TRAILER: "Anhänger",
      TOOL: "Werkzeug",
      VEHICLE: "Fahrzeug",
      EQUIPMENT: "Gerät",
      MACHINE: "Maschine",
      HEBEBUEHNE: "Hebebühne",
      ARBEITSPLATZ: "Arbeitsplatz",
    };
    return names[type] || type;
  };

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: { xs: 2, md: 3 },
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        border: "1px solid rgba(0,0,0,0.06)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: { xs: "none", md: "translateY(-4px)" },
          boxShadow: {
            xs: "0 4px 16px rgba(0,0,0,0.12)",
            md: "0 8px 32px rgba(0,0,0,0.15)",
          },
          borderColor: "primary.light",
        },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #2196f3 0%, #1976d2 100%)",
          zIndex: 1,
        },
      }}
    >
      <CardContent
        sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, pt: { xs: 2.5, md: 3.5 } }}
      >
        {/* Object Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={{ xs: 1.5, md: 2 }}
        >
          <Box flex={1}>
            <Typography
              variant="h6"
              component="h3"
              gutterBottom
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                fontWeight: 700,
                color: "primary.main",
                wordBreak: "break-word",
                mb: { xs: 1, md: 1.5 },
                lineHeight: 1.3,
              }}
            >
              {rentalObject.name}
            </Typography>
            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
              <Chip
                label={getTypeDisplayName(rentalObject.type)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                  height: { xs: 22, md: 24 },
                  fontWeight: 600,
                  borderWidth: 1.5,
                }}
              />
              <Chip
                label={formatCurrency(rentalObject.basePrice || 0)}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                  height: { xs: 22, md: 24 },
                  fontWeight: 600,
                  borderWidth: 1.5,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 1.5, md: 2 } }} />

        {/* Bundles */}
        {bundles.length > 0 ? (
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: "text.primary",
                fontWeight: 700,
                mb: { xs: 1, md: 1.5 },
                fontSize: { xs: "0.9rem", md: "1rem" },
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Bundle-Artikel
              <Chip
                label={bundles.length}
                size="small"
                color="info"
                sx={{
                  fontSize: "0.7rem",
                  height: 20,
                  minWidth: 20,
                }}
              />
            </Typography>
            <List disablePadding>
              {bundles.map((bundle, index) => (
                <React.Fragment key={bundle.id}>
                  <ListItem
                    sx={{
                      px: { xs: 1, md: 2 },
                      py: { xs: 0.75, md: 1 },
                      backgroundColor: bundle.isActive
                        ? "transparent"
                        : "grey.50",
                      borderRadius: { xs: 1, md: 1.5 },
                      mb: 0.5,
                      border: "1px solid",
                      borderColor: bundle.isActive ? "transparent" : "grey.200",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: bundle.isActive
                          ? "primary.light"
                          : "grey.100",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              textDecoration: bundle.isActive
                                ? "none"
                                : "line-through",
                              color: bundle.isActive
                                ? "text.primary"
                                : "text.disabled",
                              fontSize: { xs: "0.85rem", md: "0.9rem" },
                            }}
                          >
                            {bundle.article?.name || "Unbekannter Artikel"}
                          </Typography>
                          {/* Bundle isPublic Icon */}
                          {bundle.isPublic ? (
                            <VisibilityIcon
                              color="success"
                              sx={{ fontSize: { xs: 14, md: 16 } }}
                              titleAccess="Bundle ist öffentlich"
                            />
                          ) : (
                            <VisibilityOffIcon
                              color="disabled"
                              sx={{ fontSize: { xs: 14, md: 16 } }}
                              titleAccess="Bundle ist privat"
                            />
                          )}

                        </Box>
                      }
                      secondary={
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={0.5}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.8rem" },
                              fontWeight: 500,
                            }}
                          >
                            {formatCurrency(bundle.bundlePrice)} pro Mieteinheit
                          </Typography>
                          {bundle.notes && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                maxWidth: { xs: 80, md: 120 },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: { xs: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              {bundle.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box display="flex" gap={{ xs: 0.25, md: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => onEditBundle(bundle)}
                          sx={{
                            color: "primary.main",
                            "&:hover": {
                              bgcolor: "primary.light",
                              color: "white",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            onRemoveBundle(bundle.id!, rentalObject.id)
                          }
                          sx={{
                            color: "error.main",
                            "&:hover": {
                              bgcolor: "error.light",
                              color: "white",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < bundles.length - 1 && (
                    <Divider
                      variant="inset"
                      component="li"
                      sx={{
                        my: 0.5,
                        opacity: 0.6,
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={{ xs: 2.5, md: 3 }}
            sx={{
              backgroundColor: "grey.50",
              borderRadius: { xs: 1.5, md: 2 },
              border: "2px dashed",
              borderColor: "primary.light",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{
                fontSize: { xs: "0.85rem", md: "0.9rem" },
                fontWeight: 500,
              }}
            >
              Keine Bundle-Artikel
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              textAlign="center"
              mt={0.5}
              sx={{
                fontSize: { xs: "0.75rem", md: "0.8rem" },
                opacity: 0.8,
              }}
            >
              Wählen Sie dieses Objekt aus, um Bundle-Artikel hinzuzufügen
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BundleListCard;
